import React from 'react';
import ReactDOM from 'react-dom/client';
import { isOnPublicAuthRoute } from './utils/authPublicRoutes';
import './i18n';
import './styles/themeTokens.css';
import './index.css';
import './styles/responsive/globalResponsive.css'; // ✅ Styles responsive globaux
import App from './App';
import reportWebVitals from './reportWebVitals';
import ErrorBoundary from './components/Common/ErrorBoundary';
import { Analytics } from '@vercel/analytics/react';
import { applyUserPreferences, getUserPreferences } from './services/userPreferences';

applyUserPreferences(getUserPreferences());

// 🔇 Supprimer console.log en production (debug logs non destinés aux utilisateurs)
if (process.env.NODE_ENV === 'production') {
  // eslint-disable-next-line no-console
  console.log = () => {};
}

// Bruit connu : extensions navigateur (scripts injectés hors bundle UCP).
function isBenignExternalRuntimeNoise({ message = '', reason = '', filename = '', stack = '' } = {}) {
  const blob = [message, reason, filename, stack]
    .map((s) => String(s || '').toLowerCase())
    .join('\n');
  if (
    blob.includes('chrome-extension://') ||
    blob.includes('moz-extension://') ||
    blob.includes('safari-web-extension://')
  ) {
    return true;
  }
  const needles = [
    'tabs:outgoing.message.ready',
    'no listener',
    'message channel closed',
    'receiving end does not exist',
    'extension context invalidated',
    'could not establish connection',
    'the message port closed before a response was received'
  ];
  return needles.some((n) => blob.includes(n));
}

// ✅ Masquer l'overlay d'erreur React en développement - SUPPRESSION IMMÉDIATE
if (process.env.NODE_ENV === 'development') {
  // Observer pour supprimer immédiatement tout overlay qui apparaît
  const observer = new MutationObserver(() => {
    const overlays = document.querySelectorAll(
      '[data-react-error-overlay], #react-error-overlay, iframe[src*="react-error-overlay"], [class*="error-overlay"]'
    );
    overlays.forEach(overlay => {
      if (overlay instanceof HTMLElement) {
        overlay.remove(); // Supprimer immédiatement
      }
    });
  });
  
  // Observer le body dès que possible
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
}

// ✅ Gestionnaire d'erreur global pour capturer les erreurs JavaScript
// ✅ SUPPRESSION COMPLÈTE de l'affichage des erreurs "Script error"
window.addEventListener('error', (event) => {
  // Si c'est une erreur "Script error" (CORS), la masquer complètement
  if (event.message === 'Script error.' || 
      event.message === 'Uncaught Error: Script error.' ||
      event.message.includes('Script error')) {
    
    // Logger dans la console uniquement (pas d'overlay)
    console.warn('⚠️ Erreur "Script error" détectée et masquée');
    
    // Empêcher l'affichage de l'overlay React
    event.stopImmediatePropagation();
    event.preventDefault();
    
    // Masquer tous les overlays existants
    setTimeout(() => {
      const overlays = document.querySelectorAll(
        '[data-react-error-overlay], #react-error-overlay, iframe[src*="react-error-overlay"], [class*="error-overlay"]'
      );
      overlays.forEach(overlay => {
        if (overlay instanceof HTMLElement) {
          overlay.remove(); // Supprimer complètement au lieu de masquer
        }
      });
    }, 10);
    
    return false;
  }

  if (
    isBenignExternalRuntimeNoise({
      message: event.message,
      filename: event.filename,
      stack: event.error?.stack
    })
  ) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return false;
  }

  // Logger les autres erreurs mais ne pas bloquer l'app
  console.error('❌ Erreur JavaScript:', event.message);
  event.preventDefault();
  return false;
});

// ✅ Gestionnaire pour les promesses rejetées non gérées
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const msg =
    typeof reason === 'string'
      ? reason
      : reason && typeof reason.message === 'string'
        ? reason.message
        : String(reason || '');
  const stack = reason && typeof reason === 'object' && reason.stack ? reason.stack : '';

  if (isBenignExternalRuntimeNoise({ message: msg, reason: msg, stack })) {
    event.preventDefault();
    return false;
  }

  console.warn('⚠️ Promesse rejetée:', reason);
  event.preventDefault();

  // Masquer l'overlay si c'est lié à "Script error"
  setTimeout(() => {
    const overlays = document.querySelectorAll(
      '[data-react-error-overlay], #react-error-overlay, iframe[src*="react-error-overlay"]'
    );
    overlays.forEach(overlay => {
      if (overlay instanceof HTMLElement) {
        overlay.remove();
      }
    });
  }, 10);

  return false;
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
      <Analytics />
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

const isServiceWorkerSupported = 'serviceWorker' in navigator;
const shouldRegisterServiceWorker = isServiceWorkerSupported;

if (shouldRegisterServiceWorker) {
  window.addEventListener('load', () => {
    let hasControllerReloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Recharge automatique unique quand le nouveau SW prend le contrôle,
      // indispensable sur mobile/tablette sans accès DevTools.
      // Lot 6 : pas de reload sur login (évite reloads perçus comme boucle au deploy).
      if (hasControllerReloaded) return;
      if (isOnPublicAuthRoute(window.location.pathname)) return;
      hasControllerReloaded = true;
      window.location.reload();
    });

    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        // Force a check for latest worker on each load.
        registration.update().catch(() => {});

        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        registration.addEventListener('updatefound', () => {
          const nextWorker = registration.installing;
          if (!nextWorker) return;
          nextWorker.addEventListener('statechange', () => {
            if (nextWorker.state === 'installed' && navigator.serviceWorker.controller) {
              nextWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((error) => {
        console.warn('Service Worker registration failed:', error);
      });
  });
}
